import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import Modal from "react-modal";
import { ChevronDown, ChevronUp, LogOut, Menu, X } from "lucide-react";

import styles from "../sidebar/sidebar.module.scss";
import mark from "../../images/penspace-mark.png";
import { usePermissions } from "../../context/permissionContext";
import { useLanguage } from "../../context/languageContext";
import penspace from "../../images/penspace-logo.png";
import { ALL_GROUP_IDS, NAV_SECTIONS, NAV_SINGLES } from "./navSections";
import SectionTabs from "./SectionTabs";
import { formatCount, useDashboardCounts } from "./useDashboardCounts";

// Which groups are folded open, remembered across route changes. The sidebar
// remounts on every navigation, so without this every section would spring
// back open the moment you clicked a link inside one.
const OPEN_SECTIONS_KEY = "penspace.admin.openSections";

// Everything open: the design shows it that way, and a sidebar that starts
// folded hides what the panel can do. Nested groups count too, or a group
// inside a section would start folded while its section did not.
const defaultOpenSections = () =>
  Object.fromEntries(ALL_GROUP_IDS.map((id) => [id, true]));

const readOpenSections = () => {
  try {
    const raw = localStorage.getItem(OPEN_SECTIONS_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    // MERGED over the defaults, not used in place of them. Everyone who has
    // used the panel before has a stored object written before these ids
    // existed, and a missing id reads as folded — so a regrouping of the nav
    // would arrive with its new groups shut for every returning admin and
    // open only for someone who had never signed in.
    if (saved && typeof saved === "object") return { ...defaultOpenSections(), ...saved };
  } catch (_) {
    // Corrupt or unavailable storage (private mode) — fall through to the
    // default rather than taking the nav down with it.
  }
  return defaultOpenSections();
};

// Whether the drawer is held open. Hover alone cannot express it: the sidebar
// remounts on every navigation, so a pin kept only in state would drop the
// moment you clicked a link inside the drawer you had just pinned.
const PINNED_KEY = "penspace.admin.sidebarPinned";

const readPinned = () => {
  try {
    return localStorage.getItem(PINNED_KEY) === "1";
  } catch (_) {
    // Private mode / storage disabled — fall back to hover-only.
    return false;
  }
};

export const SideNavBar = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAny, loading: permissionsLoading } = usePermissions();

  const counts = useDashboardCounts();
  const { adminInfo } = useLanguage();

  // Name, address, and the initials to stand in for an avatar we do not have.
  // Every field is optional in practice — an older stored session may predate
  // the login response carrying them — so each falls back rather than
  // rendering "undefined" at someone.
  const signedIn = useMemo(() => {
    const name = adminInfo?.userName?.trim() || "Admin";
    const initials = name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    return {
      name,
      initials: initials || "A",
      meta: adminInfo?.email?.trim() || adminInfo?.role || "",
    };
  }, [adminInfo?.userName, adminInfo?.email, adminInfo?.role]);

  // Hide what the admin has no access to. While permissions are still loading
  // the full menu is kept so the sidebar doesn't flicker. A section whose every
  // row is gated away goes with them — an empty heading explains nothing.
  // `feature` may be a list — the row shows when ANY of them is granted (Trash
  // holds several features' content).
  const allowed = useCallback(
    (entry) => permissionsLoading || !entry.feature || [].concat(entry.feature).some(canAny),
    [canAny, permissionsLoading],
  );

  // A group is kept only for the rows inside it the admin may actually see,
  // and disappears entirely when that is none — a heading over nothing
  // explains nothing, at either level.
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items
          .map((item) =>
            item.children
              ? { ...item, children: item.children.filter(allowed) }
              : item,
          )
          .filter((item) =>
            item.children ? item.children.length > 0 : allowed(item),
          ),
      })).filter((section) => section.items.length > 0),
    [allowed],
  );

  // Singles may be tabbed groups too (Team), filtered the same way.
  const visibleSingles = useMemo(
    () =>
      NAV_SINGLES.map((item) =>
        item.children
          ? { ...item, children: item.children.filter(allowed) }
          : item,
      ).filter((item) =>
        item.children ? item.children.length > 0 : allowed(item),
      ),
    [allowed],
  );

  const [openSections, setOpenSections] = useState(readOpenSections);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(openSections));
    } catch (_) {
      // Storage unavailable — the fold state just won't outlive this page.
    }
  }, [openSections]);

  const toggleSection = useCallback((id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const [isSideBarHovered, setIsSideBarHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(readPinned);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isShowLogoutVisible, setIsShowLogoutVisible] = useState(false);

  const isMenuActive = useCallback(
    (path, end) => {
      if (path === location.pathname) return true;
      if (end) return false;
      return location.pathname.startsWith(path + "/");
    },
    [location.pathname],
  );

  // Landing on a page inside a folded group or section unfolds it — otherwise
  // the nav shows no trace of where you are. Keyed on the path, not applied on
  // every render: forcing it open continuously made the group's own chevron
  // look broken, because a click to fold was undone before it painted.
  useEffect(() => {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        if (!item.children || item.tabbed) continue;
        if (!item.children.some((child) => isMenuActive(child.path, child.end))) continue;
        setOpenSections((prev) =>
          prev[item.id] && prev[section.id]
            ? prev
            : { ...prev, [item.id]: true, [section.id]: true },
        );
        return;
      }
    }
  }, [location.pathname, isMenuActive]);

  const handleSidebarMouseEnter = useCallback(
    () => setIsSideBarHovered(true),
    [],
  );
  const handleSidebarMouseLeave = useCallback(
    () => setIsSideBarHovered(false),
    [],
  );

  const handleLogout = useCallback(() => {
    setIsShowLogoutVisible(false);
    setIsMobileOpen(false);
    localStorage.removeItem("user");
    navigate("/auth");
  }, [navigate]);

  const handleCloseLogout = useCallback(
    () => setIsShowLogoutVisible(false),
    [],
  );

  const handleMobileMenuToggle = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const handleMobileLinkClick = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  // Clicking the logo both navigates to the dashboard and holds the drawer
  // open, so the nav stays put while you work instead of folding away the
  // moment the pointer leaves it.
  const handleLogoClick = useCallback(() => {
    handleMobileLinkClick();
    setIsPinned(true);
  }, [handleMobileLinkClick]);

  const handleUnpin = useCallback(() => {
    setIsPinned(false);
    // Without this the drawer stays open until the pointer leaves it — the
    // cursor is still inside the sidebar at the instant of the click, so
    // hover alone would keep it expanded and the button would read as dead.
    setIsSideBarHovered(false);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(PINNED_KEY, isPinned ? "1" : "0");
    } catch (_) {
      // Not persisting a preference is not worth taking the nav down for.
    }
  }, [isPinned]);

  const logoutPopupStyles = useMemo(
    () => ({
      content: {
        width: "90%",
        maxWidth: "420px",
        height: "auto",
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        border: "none",
        borderRadius: "20px",
        padding: "0",
        background: "transparent",
        boxShadow: "none",
        outline: "none",
        overflow: "hidden",
      },
      overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        zIndex: 1050,
      },
    }),
    [],
  );

  /**
   * WHICH ROW IS LIT — decided once, for the whole nav.
   *
   * Every row used to answer that for itself, by testing the current path
   * against its own. Two rows could therefore both say yes, and the panel
   * showed exactly that: User Interactions stayed filled green after moving to
   * an AI page, so the nav claimed you were in two places at once.
   *
   * One winner is picked here instead. The most specific match wins — an exact
   * path beats a prefix, and a longer prefix beats a shorter one — so nothing
   * downstream can disagree, whatever shape the nav grows into next.
   */
  const activeKey = useMemo(() => {
    const candidates = [];
    const add = (key, path, end) => { if (path) candidates.push({ key, path, end }); };

    for (const section of visibleSections) {
      for (const item of section.items) {
        // A tabbed group is lit by any of its pages; a fold-out lights its own
        // rows, not its header.
        if (item.children) {
          if (item.tabbed) item.children.forEach((c) => add(`group:${item.id}`, c.path, c.end));
          else item.children.forEach((c) => add(`link:${c.path}`, c.path, c.end));
        } else add(`link:${item.path}`, item.path, item.end);
      }
    }
    visibleSingles.forEach((item) => {
      if (item.tabbed) item.children.forEach((c) => add(`group:${item.id}`, c.path, c.end));
      else add(`link:${item.path}`, item.path, item.end);
    });

    const path = location.pathname;
    let best = null;
    for (const c of candidates) {
      const exact = c.path === path;
      const prefix = !c.end && path.startsWith(c.path + "/");
      if (!exact && !prefix) continue;
      // Exact beats prefix; a longer prefix beats a shorter one.
      const score = exact ? Infinity : c.path.length;
      if (!best || score > best.score) best = { key: c.key, score };
    }
    return best?.key ?? null;
  }, [visibleSections, visibleSingles, location.pathname]);

  // One row, shared by the grouped items and the standalone ones.
  const renderLink = (item) => {
    const Icon = item.icon;
    const active = activeKey === `link:${item.path}`;
    const count = formatCount(counts?.[item.countKey]);
    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          end={item.end}
          // A FUNCTION, not a string. Given a string, NavLink appends its own
          // literal "active" class when the path matches — the row deciding
          // for itself again, which is the behaviour `activeKey` replaces.
          // A function className suppresses that entirely.
          className={() => `${styles.menuItem} ${active ? styles.active : ""}`}
          onClick={handleMobileLinkClick}
          aria-current={active ? "page" : undefined}
          title={item.name}
        >
          {Icon ? (
            <span className={styles.icon} aria-hidden>
              <Icon size={18} strokeWidth={2} />
            </span>
          ) : (
            <span className={styles.iconSpacer} aria-hidden />
          )}
          <span className={styles.itemName}>{item.name}</span>
          {count && <span className={styles.count}>{count}</span>}
        </NavLink>
        <span className={styles.tooltip} role="tooltip">
          {item.name}
        </span>
      </li>
    );
  };

  // A group inside a section: the same row, with a chevron instead of a link,
  // and its own entry in the fold state.
  const renderGroup = (group) => {
    const Icon = group.icon;
    const expanded = Boolean(openSections[group.id]);
    const panelId = `nav-group-${group.id}`;

    return (
      <li key={group.id} className={styles.group}>
        <button
          type="button"
          className={`${styles.menuItem} ${styles.groupHeader}`}
          onClick={() => toggleSection(group.id)}
          aria-expanded={expanded}
          aria-controls={panelId}
        >
          {Icon ? (
            <span className={styles.icon} aria-hidden>
              <Icon size={18} strokeWidth={2} />
            </span>
          ) : (
            <span className={styles.iconSpacer} aria-hidden />
          )}
          <span className={styles.itemName}>{group.name}</span>
          <span className={styles.chevron} aria-hidden>
            {expanded ? (
              <ChevronUp size={16} strokeWidth={2} />
            ) : (
              <ChevronDown size={16} strokeWidth={2} />
            )}
          </span>
        </button>
        <span className={styles.tooltip} role="tooltip">
          {group.name}
        </span>
        <ul
          id={panelId}
          className={`${styles.menuItems} ${styles.groupItems}`}
          hidden={!expanded}
        >
          {group.children.map(renderLink)}
        </ul>
      </li>
    );
  };

  // A tabbed group: one row, linking to the first of its pages this admin can
  // see, and lit while any of them is open. Its pages switch between each
  // other from SectionTabs above the content rather than from the sidebar.
  const renderTabbedGroup = (group) => {
    const Icon = group.icon;
    const first = group.children[0];
    const active = activeKey === `group:${group.id}`;
    return (
      <li key={group.id}>
        {/* A plain Link: NavLink would compute "current" from first.path
            alone and drop it on the group's other pages. */}
        <Link
          to={first.path}
          className={`${styles.menuItem} ${active ? styles.active : ""}`}
          onClick={handleMobileLinkClick}
          aria-current={active ? "page" : undefined}
          title={group.name}
        >
          {Icon ? (
            <span className={styles.icon} aria-hidden>
              <Icon size={18} strokeWidth={2} />
            </span>
          ) : (
            <span className={styles.iconSpacer} aria-hidden />
          )}
          <span className={styles.itemName}>{group.name}</span>
        </Link>
        <span className={styles.tooltip} role="tooltip">
          {group.name}
        </span>
      </li>
    );
  };

  const renderEntry = (item) =>
    item.children
      ? item.tabbed
        ? renderTabbedGroup(item)
        : renderGroup(item)
      : renderLink(item);

  // The tabbed group the current page belongs to, already filtered to the
  // pages this admin may open — so a tab never leads to a 403.
  const activeTabbedGroup = useMemo(
    () =>
      [...visibleSections.flatMap((section) => section.items), ...visibleSingles]
        .find(
          (item) =>
            item.tabbed &&
            item.children.some((child) => isMenuActive(child.path, child.end)),
        ),
    [visibleSections, visibleSingles, isMenuActive],
  );

  // Deliberately a value, not a `() => (...)` component defined in the body:
  // that is a new component type on every render, so React threw the whole
  // sidebar away and rebuilt it each time a section folded — losing focus and
  // any transition mid-flight.
  const navContent = (
    <>
      {/* The close button cannot live inside the NavLink — an anchor may not
          contain a button — so the two are siblings in a header row. */}
      <div className={styles.logoRow}>
        <NavLink
          to="/admin"
          className={styles.logoWrapper}
          onClick={handleLogoClick}
          title="Dashboard"
        >
          <img
            className={styles.mark}
            src={mark}
            alt=""
            width={32}
            height={32}
          />
          <img
            className={styles.penspaceLogo}
            src={penspace}
            alt="Penspace"
            width={104}
            height={26}
          />
        </NavLink>
        {/* Only while pinned: unpinning a drawer that is merely open under the
            pointer would do nothing visible, so the affordance would lie. */}
        {isPinned && (
          <button
            type="button"
            className={styles.pinCloseBtn}
            onClick={handleUnpin}
            aria-label="Minimise sidebar"
          >
            <X size={18} />
          </button>
        )}
      </div>
      <div className={styles.separator} />
      <nav className={styles.nav} aria-label="Main navigation">
        {visibleSections.map((section) => {
          const SectionIcon = section.icon;
          const open = Boolean(openSections[section.id]);
          const panelId = `nav-section-${section.id}`;
          return (
            <div className={styles.section} key={section.id}>
              {/* The whole heading is the toggle, not just the chevron — a
                  20px arrow is a small thing to ask someone to hit. */}
              <button
                type="button"
                className={styles.sectionHeader}
                onClick={() => toggleSection(section.id)}
                aria-expanded={open}
                aria-controls={panelId}
              >
                <span className={styles.icon} aria-hidden>
                  <SectionIcon size={18} strokeWidth={2} />
                </span>
                <span className={styles.sectionName}>{section.name}</span>
                {/* Up when the section is open, down when it is folded: the
                    arrow points the way the click will move the list. */}
                <span className={styles.chevron} aria-hidden>
                  {open ? (
                    <ChevronUp size={16} strokeWidth={2} />
                  ) : (
                    <ChevronDown size={16} strokeWidth={2} />
                  )}
                </span>
              </button>
              <span className={styles.tooltip} role="tooltip">
                {section.name}
              </span>
              <ul
                id={panelId}
                className={styles.menuItems}
                // `hidden` rather than unmounting, so the browser keeps the
                // rows out of the tab order and the accessibility tree while
                // they are folded away.
                hidden={!open}
              >
                {section.items.map(renderEntry)}
              </ul>
            </div>
          );
        })}

        {visibleSingles.length > 0 && (
          <ul className={`${styles.menuItems} ${styles.singles}`}>
            {visibleSingles.map(renderEntry)}
          </ul>
        )}
      </nav>
      <div className={styles.footer}>
        <div
          className={styles.userCard}
          title={`${signedIn.name}${signedIn.meta ? ` · ${signedIn.meta}` : ""}`}
        >
          <span className={styles.avatar} aria-hidden>
            {signedIn.initials}
          </span>
          <span className={styles.userText}>
            <span className={styles.userName}>{signedIn.name}</span>
            {signedIn.meta && (
              <span className={styles.userMeta}>{signedIn.meta}</span>
            )}
          </span>
        </div>
        <div className={styles.logoutSection}>
          <button
            type="button"
            onClick={() => setIsShowLogoutVisible(true)}
            className={styles.logoutButton}
            aria-label="Open logout confirmation"
          >
            <LogOut size={20} strokeWidth={2} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <React.Fragment>
      <button
        type="button"
        className={styles.mobileMenuBtn}
        onClick={handleMobileMenuToggle}
        aria-label={isMobileOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMobileOpen}
        aria-controls="sidebar-nav"
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {isMobileOpen && (
        <button
          type="button"
          className={styles.overlay}
          onClick={handleMobileMenuToggle}
          aria-label="Close menu"
        />
      )}

      <div className={styles.layout}>
        <div
          className={`${styles.sidebarWrapper} ${!isSideBarHovered && !isPinned ? styles.collapsed : ""} ${isMobileOpen ? styles.mobileOpen : ""}`}
        >
          <aside
            id="sidebar-nav"
            className={styles.aside}
            onMouseEnter={handleSidebarMouseEnter}
            onMouseLeave={handleSidebarMouseLeave}
            aria-label="Sidebar"
          >
            {navContent}
          </aside>
        </div>
        <main className={styles.main}>
          <div className={styles.childContent}>
            {activeTabbedGroup && <SectionTabs group={activeTabbedGroup} />}
            {children}
          </div>
        </main>
      </div>

      <Modal
        isOpen={isShowLogoutVisible}
        onRequestClose={handleCloseLogout}
        style={logoutPopupStyles}
      >
        <div className={styles.logoutModal}>
          <div className={styles.logoutHeader}>
            <div className={styles.logoutIcon}>
              <LogOut size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.logoutTitle}>Logout</h3>
            <p className={styles.logoutMessage}>
              Are you sure you want to logout from your admin panel?
            </p>
          </div>

          <div className={styles.logoutActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCloseLogout}
            >
              No, Cancel
            </button>
            <button
              type="button"
              className={styles.logoutConfirmButton}
              onClick={handleLogout}
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default SideNavBar;
